/**
 * Audit action configuration - single source of truth for audit action display.
 *
 * When adding new audit actions to the backend (in firestore.ts AuditAction type),
 * add corresponding entries here to ensure proper display in the UI.
 */

export interface AuditActionConfig {
  label: string;
  color: string;
  category: "app" | "install" | "user" | "auth" | "invitation" | "token" | "webhook";
}

/**
 * All audit action types with their display configuration.
 * This must be kept in sync with backend/src/services/firestore.ts AuditAction type.
 */
export const AUDIT_ACTIONS: Record<string, AuditActionConfig> = {
  // App actions
  "app.create": {
    label: "Created App",
    color: "bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300",
    category: "app",
  },
  "app.update": {
    label: "Updated App",
    color: "bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300",
    category: "app",
  },
  "app.delete": {
    label: "Deleted App",
    color: "bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300",
    category: "app",
  },

  // Install actions
  "install.create": {
    label: "App Installed",
    color: "bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300",
    category: "install",
  },
  "install.reauthorize": {
    label: "App Re-authorized",
    color: "bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300",
    category: "install",
  },
  "install.uninstall": {
    label: "App Uninstalled",
    color: "bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300",
    category: "install",
  },
  "install.webhook_uninstall": {
    label: "Webhook Uninstall",
    color: "bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300",
    category: "install",
  },

  // Invitation actions
  "invitation.resend": {
    label: "Resent Invite",
    color: "bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300",
    category: "invitation",
  },
  "invitation.cancel": {
    label: "Cancelled Invite",
    color: "bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300",
    category: "invitation",
  },

  // Token actions
  "token.revoke": {
    label: "Revoked Token",
    color: "bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300",
    category: "token",
  },

  // Webhook actions
  "webhook.register": {
    label: "Registered Webhook",
    color: "bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300",
    category: "webhook",
  },

  // User actions
  "user.invite": {
    label: "Invited User",
    color: "bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300",
    category: "user",
  },
  "user.role_change": {
    label: "Changed Role",
    color: "bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300",
    category: "user",
  },
  "user.remove": {
    label: "Removed User",
    color: "bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300",
    category: "user",
  },

  // Auth actions
  "auth.login": {
    label: "Logged In",
    color: "bg-gray-100 dark:bg-dark-bg-tertiary text-gray-800 dark:text-dark-text-secondary",
    category: "auth",
  },
  "auth.logout": {
    label: "Logged Out",
    color: "bg-gray-100 dark:bg-dark-bg-tertiary text-gray-800 dark:text-dark-text-secondary",
    category: "auth",
  },
} as const;

/**
 * Default styling for unknown action types (fallback)
 */
export const DEFAULT_ACTION_CONFIG: AuditActionConfig = {
  label: "Unknown Action",
  color: "bg-gray-100 dark:bg-dark-bg-tertiary text-gray-800 dark:text-dark-text-secondary",
  category: "app",
};

/**
 * Get the display configuration for an audit action.
 * Returns default config if the action type is not recognized.
 */
export function getAuditActionConfig(action: string): AuditActionConfig {
  return AUDIT_ACTIONS[action] || { ...DEFAULT_ACTION_CONFIG, label: action };
}

/**
 * Get all audit actions as options for a dropdown/select.
 * Returns array of { value, label } sorted by label.
 */
export function getAuditActionOptions(): Array<{ value: string; label: string }> {
  return Object.entries(AUDIT_ACTIONS)
    .map(([value, config]) => ({ value, label: config.label }))
    .sort((a, b) => a.label.localeCompare(b.label));
}
